import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ReelMineViewModel } from 'api/types/reel';

const formatDateTr = (iso?: string | null): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString('tr-TR');
};

function isValidVideoUrl(url: string): boolean {
  const trimmed = url.trim();
  return trimmed.length > 0 && (trimmed.startsWith('http://') || trimmed.startsWith('https://'));
}

export interface MyReelCardProps {
  reel: ReelMineViewModel;
  onDelete?: (reel: ReelMineViewModel) => void;
  onPlay?: (reel: ReelMineViewModel) => void;
  deleteDisabled?: boolean;
}

const MyReelCard: React.FC<MyReelCardProps> = ({ reel, onDelete, onPlay, deleteDisabled = false }) => {
  const hasListing = Boolean(reel.listingId && reel.listingTitle && reel.listingCode);
  const isPending = reel.statusId === 4;
  const isRejected = reel.statusId === 5;
  const [mediaState, setMediaState] = useState<'loading' | 'ready' | 'error'>(() =>
    isValidVideoUrl(reel.videoUrl) ? 'loading' : 'error'
  );

  useEffect(() => {
    setMediaState(isValidVideoUrl(reel.videoUrl) ? 'loading' : 'error');
  }, [reel.videoUrl]);

  return (
    <div className="card h-100 shadow-sm border-0">
      <div
        className="card-img-top bg-dark overflow-hidden position-relative"
        style={{ height: 220 }}
      >
        {isValidVideoUrl(reel.videoUrl) && mediaState !== 'error' && (
          <video
            src={reel.videoUrl}
            muted
            preload="metadata"
            playsInline
            onLoadedMetadata={() => setMediaState('ready')}
            onCanPlay={() => setMediaState('ready')}
            onError={() => setMediaState('error')}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: mediaState === 'ready' ? 'block' : 'none',
            }}
          />
        )}

        {mediaState === 'loading' && (
          <div className="position-absolute top-50 start-50 translate-middle text-center text-white-50">
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
          </div>
        )}

        {mediaState === 'error' && (
          <div className="position-absolute top-50 start-50 translate-middle text-center text-white-50 px-2">
            <i className="fas fa-exclamation-triangle mb-1" aria-hidden="true" />
            <div className="small">Video yüklenemedi</div>
          </div>
        )}

        {onPlay && mediaState === 'ready' && (
          <button
            type="button"
            className="btn position-absolute top-50 start-50 translate-middle rounded-circle d-flex align-items-center justify-content-center"
            style={{ width: 48, height: 48, background: 'rgba(0,0,0,0.55)', border: 'none' }}
            onClick={() => onPlay(reel)}
            aria-label="Videoyu oynat"
          >
            <i className="fas fa-play text-white" style={{ marginLeft: 3 }} />
          </button>
        )}

        {onDelete && (
          <button
            type="button"
            className="btn btn-danger btn-sm position-absolute top-0 end-0 m-2"
            onClick={() => onDelete(reel)}
            disabled={deleteDisabled}
            title="Gönderiyi sil"
            aria-label="Gönderiyi sil"
          >
            <i className="fas fa-trash" />
          </button>
        )}
      </div>
      <div className="card-body">
        {(isPending || isRejected) && (
          <div className="mb-2">
            {isPending && <span className="badge bg-warning text-dark">Onay bekliyor</span>}
            {isRejected && <span className="badge bg-danger">Reddedildi</span>}
          </div>
        )}
        {isRejected && reel.rejectionReason && (
          <p className="card-text small text-danger mb-2">{reel.rejectionReason}</p>
        )}
        <p className="card-text small text-muted mb-2">
          <i className="fas fa-calendar-alt me-1" />
          Yüklenme: {formatDateTr(reel.createdAtUtc)}
        </p>
        {hasListing ? (
          <p className="card-text small mb-0">
            <i className="fas fa-tag me-1 text-primary" />
            İlan:{' '}
            <Link
              to={`/ilan-detay/${reel.listingCode}`}
              className="text-decoration-none"
            >
              {reel.listingTitle}
            </Link>
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default MyReelCard;
